/**
 * Message List Component
 * Displays list of messages with auto-scroll. Long lists are virtualized via
 * react-virtuoso so the message bubble DOM stays bounded by the viewport
 * instead of growing linearly with conversation length.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Message } from './Message';
import { cn } from '@/utils/classNames';
import { usePreferences } from '@/context/PreferencesContext';
import type { Message as MessageType, MessageFeedback } from '@/types';

// Past this many seconds without a response, show an elapsed-time hint
// next to the typing dots. Differentiates "the model is genuinely
// thinking" from "the UI froze" — important for Azure Model Router
// routes that land on o-series reasoning models.
export const ELAPSED_HINT_THRESHOLD_MS = 10_000;

export interface MessageListProps {
  messages: MessageType[];
  isLoading?: boolean;
  /**
   * Epoch ms when the in-flight generation started. When provided and
   * `isLoading` is true, the typing indicator surfaces an elapsed-time
   * hint once the wait crosses ELAPSED_HINT_THRESHOLD_MS.
   */
  generationStartedAt?: number | null;
  className?: string;
  /** Forwarded to each Message for feedback telemetry. */
  conversationId?: string | null;
  /** Forwarded to each Message for feedback telemetry. */
  agentId?: string | null;
  /** Forwarded to each Message for feedback telemetry. */
  model?: string | null;
  /** Bubble feedback toggles up so the parent can persist them. */
  onFeedbackChange?: (
    messageId: string,
    feedback: MessageFeedback | null
  ) => void;
}

interface TypingIndicatorProps {
  startedAt?: number | null;
}

function TypingIndicator({ startedAt }: TypingIndicatorProps) {
  // Tick once per second while in flight; only after the threshold do
  // we actually render the elapsed-time hint, so most fast completions
  // never even paint it.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const elapsedMs = startedAt ? nowMs - startedAt : 0;
  const showElapsed = startedAt != null && elapsedMs >= ELAPSED_HINT_THRESHOLD_MS;
  const elapsedSec = Math.floor(elapsedMs / 1000);

  return (
    <div className="flex gap-3 px-4 pb-4 pt-4" data-testid="typing-indicator">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-text-secondary">
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      </div>
      <div className="flex items-center gap-2 rounded-2xl bg-typing-bg px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="typing-dot" />
          <span className="typing-dot" style={{ animationDelay: '0.2s' }} />
          <span className="typing-dot" style={{ animationDelay: '0.4s' }} />
        </div>
        {showElapsed && (
          <span
            className="text-xs tabular-nums text-text-tertiary"
            data-testid="typing-elapsed"
            aria-live="polite"
          >
            Still working… {elapsedSec}s
          </span>
        )}
      </div>
    </div>
  );
}

export function MessageList({
  messages,
  isLoading = false,
  generationStartedAt = null,
  className,
  conversationId = null,
  agentId = null,
  model = null,
  onFeedbackChange,
}: MessageListProps) {
  const { preferences } = usePreferences();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  // Stable identity for the Footer reference avoids unnecessary
  // Virtuoso re-measures when isLoading toggles. Declared before the
  // empty-state early-return so hook order stays consistent. We
  // intentionally close over `generationStartedAt` here: when it
  // changes the Footer identity should change too, otherwise the
  // TypingIndicator wouldn't see the new start time.
  const Footer = useMemo(
    () =>
      isLoading
        ? () => <TypingIndicator startedAt={generationStartedAt} />
        : undefined,
    [isLoading, generationStartedAt]
  );

  // Force scroll-to-bottom on every new message (user OR assistant).
  // `followOutput` alone only sticks to the bottom if the user is already
  // there at the moment the item arrives — but the user-send smooth-scroll
  // animation can still be running when the assistant reply lands, so
  // Virtuoso registers the user as "not at the bottom" and skips the
  // follow. Driving the scroll explicitly on each new message id covers
  // both the send and the response. `align: 'end'` is required so a tall
  // final message (e.g. a long assistant reply loaded when switching
  // conversations) has its bottom lined up with the viewport bottom —
  // without it Virtuoso defaults to aligning the top of the item to the
  // top of the viewport, which clips the message.
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastMessageId = lastMessage?.id ?? null;
  useEffect(() => {
    if (lastMessageId && lastMessageId !== lastMessageIdRef.current) {
      virtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'smooth',
      });
    }
    lastMessageIdRef.current = lastMessageId;
  }, [lastMessageId]);

  // Empty state
  if (messages.length === 0 && !isLoading) {
    return (
      <div className={cn('flex flex-1 items-center justify-center', className)}>
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-bg-secondary flex items-center justify-center">
            <svg
              className="h-8 w-8 text-text-tertiary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-text-primary">
            Start a conversation
          </h3>
          <p className="mt-2 text-sm text-text-secondary">
            Ask me anything about your Microsoft 365 content
          </p>
        </div>
      </div>
    );
  }

  // Per-item top padding gives us the visual spacing that the prior
  // `space-y-*` wrapper provided, without requiring Virtuoso to measure
  // collapsed margins (which it can't).
  const itemTopPadding = preferences.compactMode ? 'pt-2' : 'pt-4';

  return (
    <div
      className={cn(
        // h-full (not flex-1) because ChatInterface's content area wrapper
        // is a block container with overflow-auto, not a flex column, so
        // flex-1 here collapses to 0 height and Virtuoso renders nothing.
        'flex h-full flex-col overflow-hidden',
        className
      )}
      role="log"
      aria-live="polite"
      aria-label="Chat messages"
    >
      <div
        className="h-full min-h-0"
        data-testid="chat-column"
        data-compact={preferences.compactMode ? 'true' : 'false'}
      >
        <Virtuoso
          ref={virtuosoRef}
          data={messages}
          style={{ height: '100%' }}
          className="scrollbar-thin"
          // 'smooth' = scroll to bottom only when the user was already
          // near the bottom when a new item arrived. Matches the prior
          // reading-position-preservation behavior.
          followOutput="smooth"
          components={{ Footer }}
          itemContent={(index, message) => {
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const isGrouped = !!(
              prevMessage && prevMessage.role === message.role
            );
            return (
              <div
                className={cn('px-4', index === 0 ? 'pt-4' : itemTopPadding)}
              >
                <Message
                  message={message}
                  isGrouped={isGrouped}
                  conversationId={conversationId}
                  agentId={agentId}
                  model={model}
                  onFeedbackChange={onFeedbackChange}
                />
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
